/**
 * Gestion des numéros de carte.
 *
 * Un numéro de carte peut être :
 *  - un nombre classique  : "1", "12", "204"
 *  - un chiffre romain    : "I", "IX", "XLII" (majuscules uniquement)
 *  - autre chose / vide   : traité comme non numéroté
 *
 * Ordre d'affichage : nombres classiques d'abord (ordre croissant),
 * puis chiffres romains (ordre croissant), puis le reste (ordre alphabétique).
 */

// Chiffres romains stricts, de I (1) à MMMCMXCIX (3999), majuscules uniquement.
const ROMAN_REGEX = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/

const ROMAN_VALUES = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 }

const ROMAN_THOUSANDS = ['', 'M', 'MM', 'MMM']
const ROMAN_HUNDREDS = ['', 'C', 'CC', 'CCC', 'CD', 'D', 'DC', 'DCC', 'DCCC', 'CM']
const ROMAN_TENS = ['', 'X', 'XX', 'XXX', 'XL', 'L', 'LX', 'LXX', 'LXXX', 'XC']
const ROMAN_UNITS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']

// Catégories de tri : plus la valeur est basse, plus l'élément est affiché tôt.
export const NUMBER_KIND_ARABIC = 0
export const NUMBER_KIND_ROMAN = 1
export const NUMBER_KIND_OTHER = 2

export const ROMAN_MIN = 1
export const ROMAN_MAX = 3999

function clean(value) {
	if (value === null || value === undefined) return ''
	return String(value).trim()
}

/**
 * Indique si la valeur est un chiffre romain valide (majuscules, 1 à 3999).
 */
export function isRomanNumeral(value) {
	const raw = clean(value)
	if (!raw) return false
	return ROMAN_REGEX.test(raw)
}

/**
 * Indique si la valeur est un nombre classique (entier positif).
 */
export function isArabicNumber(value) {
	const raw = clean(value)
	if (!raw) return false
	return /^\d+$/.test(raw)
}

/**
 * Convertit un chiffre romain en entier. Retourne null si la valeur est invalide.
 */
export function romanToInt(value) {
	const raw = clean(value)
	if (!isRomanNumeral(raw)) return null

	let total = 0
	for (let i = 0; i < raw.length; i++) {
		const current = ROMAN_VALUES[raw[i]]
		const next = ROMAN_VALUES[raw[i + 1]]
		// Notation soustractive : IV, IX, XL, XC, CD, CM
		total += next && next > current ? -current : current
	}
	return total
}

/**
 * Convertit un entier (1 à 3999) en chiffre romain. Retourne null hors bornes.
 */
export function intToRoman(value) {
	const number = Number(value)
	if (!Number.isInteger(number) || number < ROMAN_MIN || number > ROMAN_MAX) return null

	return ROMAN_THOUSANDS[Math.floor(number / 1000)]
		+ ROMAN_HUNDREDS[Math.floor((number % 1000) / 100)]
		+ ROMAN_TENS[Math.floor((number % 100) / 10)]
		+ ROMAN_UNITS[number % 10]
}

/**
 * Analyse un numéro de carte et retourne sa catégorie et sa valeur de tri.
 * @returns {{ raw: string, kind: number, value: number, isRoman: boolean }}
 */
export function parseCardNumber(value) {
	const raw = clean(value)

	if (isArabicNumber(raw)) {
		return { raw, kind: NUMBER_KIND_ARABIC, value: parseInt(raw, 10), isRoman: false }
	}

	if (isRomanNumeral(raw)) {
		return { raw, kind: NUMBER_KIND_ROMAN, value: romanToInt(raw), isRoman: true }
	}

	return { raw, kind: NUMBER_KIND_OTHER, value: 0, isRoman: false }
}

/**
 * Comparateur de numéros de carte, à utiliser directement dans un `.sort()`.
 * Nombres classiques, puis chiffres romains, puis le reste.
 */
export function compareCardNumbers(a, b) {
	const parsedA = parseCardNumber(a)
	const parsedB = parseCardNumber(b)

	if (parsedA.kind !== parsedB.kind) return parsedA.kind - parsedB.kind
	if (parsedA.kind === NUMBER_KIND_OTHER) return parsedA.raw.localeCompare(parsedB.raw)
	return parsedA.value - parsedB.value
}

/**
 * Comparateur travaillant directement sur des objets carte.
 * En cas d'égalité de numéro, départage par nom.
 */
export function compareCards(a, b) {
	const byNumber = compareCardNumbers(a?.number, b?.number)
	if (byNumber !== 0) return byNumber
	return (a?.name || '').localeCompare(b?.name || '')
}

/**
 * Règle de validation Vuetify pour un champ "numéro de carte".
 */
export function cardNumberRule(value) {
	const raw = clean(value)
	if (!raw) return true
	if (isArabicNumber(raw)) return true
	if (isRomanNumeral(raw)) return true
	if (/^[IVXLCDM]+$/i.test(raw)) {
		return raw !== raw.toUpperCase()
			? 'Les chiffres romains doivent être en majuscules (ex: IX)'
			: 'Chiffre romain invalide (ex: IX, XLII, MMXXVI)'
	}
	return 'Numéro invalide : utilisez un nombre (ex: 12) ou un chiffre romain en majuscules (ex: IX)'
}

/**
 * Vrai si le numéro saisi est exploitable (vide, nombre classique ou chiffre romain).
 */
export function isValidCardNumber(value) {
	return cardNumberRule(value) === true
}

export default {
	isRomanNumeral,
	isArabicNumber,
	romanToInt,
	intToRoman,
	parseCardNumber,
	compareCardNumbers,
	compareCards,
	cardNumberRule,
	isValidCardNumber,
}
